import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Wraps content that may be showing a *previous* month/subject while the selected one loads (React
 * Query's `keepPreviousData`). While stale, the layout stays put and non-interactive; only the
 * values inside fade and blur — the boxes/frames stay crisp — so navigating months or views reads
 * as "the numbers are refreshing" instead of the whole panel going fuzzy.
 *
 * The container marks itself as the `stale` group; value elements opt into the blur with the
 * {@link staleValue} classes (which key off `group-data-[stale]`).
 */
export function StaleBlur({
  stale,
  className,
  children,
}: {
  stale: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      aria-busy={stale || undefined}
      data-stale={stale || undefined}
      className={cn('group/stale space-y-6', stale && 'pointer-events-none select-none', className)}
    >
      {children}
    </div>
  );
}

/**
 * Put these on a *value* element (a number, amount, date — not its label or card) so it blurs while
 * an ancestor {@link StaleBlur} is stale. Leaves the surrounding box/frame untouched.
 */
export const staleValue =
  'transition-[filter,opacity] duration-300 motion-reduce:transition-none ' +
  'group-data-[stale]/stale:select-none group-data-[stale]/stale:opacity-60 group-data-[stale]/stale:blur-sm';
